import React, { useState, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import {
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
} from "@material-ui/core";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ShowTicketOpen from "../ShowTicketOpenModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import StandardModal from "../ui/StandardModal";

const useStyles = makeStyles((theme) => ({
  autoComplete: {
    width: 300,
  },
  maxWidth: {
    width: "100%",
  },
  buttonColorError: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: "100%",
  },
  selectMenu: {
    height: 40,
    minHeight: 40,
    borderRadius: 12,
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 12,
    },
    "&.MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  menuItem: {
    fontSize: "0.9rem",
    height: 36,
    minHeight: 36,
    paddingTop: 4,
    paddingBottom: 4,
  },
  inputLabel: {
    fontSize: "0.9rem",
    transform: "translate(14px, 12px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  },
}));

const AcceptTicketWithouSelectQueue = ({
  modalOpen,
  onClose,
  ticketId,
  ticket,
}) => {
  const history = useHistory();
  const classes = useStyles();
  const [selectedQueue, setSelectedQueue] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");
  const { setTabOpen } = useContext(TicketsContext);
  const isMounted = useRef(true);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    try {
      if (user?.queues?.length === 1) {
        setSelectedQueue(user.queues[0].id);
      }
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  }, [selectedQueue, user?.queues]);

  const handleClose = () => {
    if (!isMounted.current) return;
    onClose();
    setSelectedQueue("");
  };

  const handleCloseAlert = () => {
    if (!isMounted.current) return;
    setOpenAlert(false);
    setLoading(false);
  };

  const handleSendMessage = async (id) => {
    if (!isMounted.current) return;

    let isGreetingMessage = false;

    try {
      const setting = await getSetting({
        column: "sendGreetingAccepted",
      });
      if (setting.sendGreetingAccepted === "enabled") isGreetingMessage = true;
    } catch (err) {
      if (isMounted.current) toastError(err);
      return;
    }

    if (!isGreetingMessage) return;

    let settingMessage;
    try {
      settingMessage = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      if (isMounted.current) toastError(err);
      return;
    }

    if (
      !settingMessage ||
      !settingMessage.greetingAcceptedMessage ||
      settingMessage.greetingAcceptedMessage.trim() === ""
    ) {
      if (isMounted.current) {
        toastError("Mensagem de saudação não configurada");
      }
      return;
    }

    if (
      (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled") &&
      ticket.status === "pending"
    ) {
      let msg = settingMessage.greetingAcceptedMessage;

      if (msg.trim() === "") {
        return;
      }
      const message = {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body: `${msg.trim()}`,
      };
      try {
        await api.post(`/messages/${id}`, message);
      } catch (err) {
        if (isMounted.current) toastError(err);
      }
    }
  };

  const handleUpdateTicketStatus = async (queueId) => {
    if (!isMounted.current) return;
    setLoading(true);

    try {
      const otherTicket = await api.put(`/tickets/${ticketId}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id || null,
        queueId: queueId,
      });

      const navigateToTicket = `/tickets/${
        otherTicket.data.uuid || ticket.uuid
      }`;
      const newTabValue =
        otherTicket.data.isGroup || ticket.isGroup ? "group" : "open";

      handleClose();

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          if (isMounted.current) {
            setOpenAlert(true);
            setUserTicketOpen(otherTicket.data.user.name);
            setQueueTicketOpen(otherTicket.data.queue.name);
            setLoading(false);
          }
        } else {
          if (isMounted.current) {
            setLoading(false);
          }
          setTabOpen(newTabValue);
          history.push(navigateToTicket);
        }
      } else {
        await handleSendMessage(ticket.id);
        if (isMounted.current) {
          setLoading(false);
        }
        setTabOpen(newTabValue);
        history.push(navigateToTicket);
      }
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  };

  return (
    <>
      <StandardModal
        open={modalOpen}
        onClose={handleClose}
        title={i18n.t("ticketsList.acceptModal.title")}
        maxWidth="xs"
        cancelText={i18n.t("ticketsList.buttons.cancel")}
        confirmationText={i18n.t("ticketsList.buttons.start")}
        onConfirm={() => handleUpdateTicketStatus(selectedQueue)}
        disableConfirmButton={selectedQueue === ""}
        loading={loading}
        useButtonSpinner={true}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel className={classes.inputLabel}>
              {i18n.t("ticketsList.acceptModal.queue")}
            </InputLabel>
            <Select
              value={selectedQueue}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedQueue(e.target.value);
              }}
              label={i18n.t("ticketsList.acceptModal.queue")}
              className={classes.selectMenu}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem value={""} className={classes.menuItem}>
                &nbsp;
              </MenuItem>
              {user?.queues?.length > 0 &&
                user.queues.map((queue) => (
                  <MenuItem
                    key={queue.id}
                    value={queue.id}
                    className={classes.menuItem}
                  >
                    {queue.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      </StandardModal>
      <ShowTicketOpen
        isOpen={openAlert}
        handleClose={handleCloseAlert}
        user={userTicketOpen}
        queue={queueTicketOpen}
      />
    </>
  );
};

export default AcceptTicketWithouSelectQueue;
